import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

// Severity-first, matching the ordering <outcomes-bar> uses everywhere else.
export const OUTCOME_ORDER: WireTypes.Outcome[] = ['regressed', 'unexpected', 'flaked', 'expected', 'skipped'];

export const OUTCOME_LABELS: Record<WireTypes.Outcome, string> = {
  regressed: 'Regressed',
  unexpected: 'Failed',
  flaked: 'Flaked',
  skipped: 'Skipped',
  expected: 'Passed',
};

// Same hue and lightening as the calendar day cells, so a swatch here reads as the
// same thing as a swatch in the calendar legend next to the chart.
export const outcomeSwatchFill = (outcome: WireTypes.Outcome) => `color-mix(in oklch, var(--fk-color-outcome-${outcome}), white 40%)`;

export class FKOutcomeSelectEvent extends Event {
  static readonly eventName = 'fk-select';
  readonly outcomes: WireTypes.Outcome[];

  constructor(outcomes: WireTypes.Outcome[]) {
    super(FKOutcomeSelectEvent.eventName, {
      bubbles: true,
      composed: true,
      cancelable: false,
    });
    this.outcomes = outcomes;
  }
}

/**
 * The color legend for <daily-metric-chart>, doubling as its series picker:
 * clicking a swatch adds or removes that outcome from the plotted stack.
 */
@customElement('outcome-legend')
export class OutcomeLegend extends LitElement {
  /** Outcomes offered as swatches, in severity order. */
  @property({ attribute: false }) outcomes: WireTypes.Outcome[] = [];
  @property({ attribute: false }) selected: WireTypes.Outcome[] = [];

  render() {
    const outcomes = OUTCOME_ORDER.filter(outcome => this.outcomes.includes(outcome));
    if (!outcomes.length)
      return nothing;
    return html`
      ${this.selected.length ? html`
        <button class=reset @click=${() => this.dispatchEvent(new FKOutcomeSelectEvent([]))}>Reset</button>
      ` : nothing}
      ${outcomes.map(outcome => {
        const isSelected = this.selected.includes(outcome);
        return html`
          <button
            class=${classMap({ swatch: true, selected: isSelected })}
            aria-pressed=${isSelected ? 'true' : 'false'}
            title=${isSelected ? `Remove ${OUTCOME_LABELS[outcome]} from the chart` : `Show ${OUTCOME_LABELS[outcome]} on the chart`}
            @click=${() => this._toggle(outcome)}
          >
            <span class=dot style="background: ${outcomeSwatchFill(outcome)};"></span>
            <span>${OUTCOME_LABELS[outcome]}</span>
          </button>
        `;
      })}
    `;
  }

  private _toggle(outcome: WireTypes.Outcome) {
    const selected = this.selected.includes(outcome)
      ? this.selected.filter(o => o !== outcome)
      : [...this.selected, outcome];
    // Keep the selection canonically ordered so it encodes to a stable URL.
    this.dispatchEvent(new FKOutcomeSelectEvent(OUTCOME_ORDER.filter(o => selected.includes(o))));
  }

  static styles = css`
    :host {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--sl-spacing-2x-small);
    }
    button {
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 2px 7px;
      border: 1px solid transparent;
      border-radius: 999px;
      background: transparent;
      font: inherit;
      font-size: 12px;
      color: var(--sl-color-neutral-500);
      cursor: pointer;
    }
    button:hover {
      background: var(--sl-color-neutral-100);
    }
    .swatch .dot {
      width: 8px;
      height: 8px;
      border-radius: 2px;
      /* Skipped is a pale neutral, so every dot gets an edge to stay visible on
         white. Unselected swatches keep their hue, just muted. */
      box-shadow: inset 0 0 0 1px var(--sl-color-neutral-400);
      opacity: 0.4;
    }
    .swatch.selected {
      border-color: var(--sl-color-neutral-300);
      background: var(--sl-color-neutral-50);
      color: var(--sl-color-neutral-700);
    }
    .swatch.selected .dot {
      opacity: 1;
    }
    .reset {
      color: var(--sl-color-neutral-400);
      text-decoration: underline;
    }
  `;
}
